import { Link } from 'react-router-dom';
import { CheckCircle, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import PageWrapper from '@/components/PageWrapper';
import ImageCarousel from '@/components/ImageCarousel';
import { FadeUp, SlideLeft, SlideRight, StaggerContainer, StaggerItem } from '@/components/ScrollReveal';

const SessionsPage = () => {
  const benefits = [
    'Fully personalised sessions tailored to your body, goals, and movement history',
    'Hands-on, detail-focused coaching to refine technique and improve movement quality',
    'Flexible programming that adapts to injuries, limitations, and progress over time',
    'A quiet, private environment that allows for deeper focus and faster results',
  ];

  const sessionFocus = [
    'Improving movement technique',
    'Supporting posture and spinal health',
    'Building strength with control',
    'Enhancing mobility and coordination',
    'Developing body awareness and confidence',
  ];

  const images = [
    { src: '/images/REFORMER HERO.jpg', alt: 'Reformer', caption: 'Reformer Work' },
    { src: '/images/CHAIR HERO 2.jpg', alt: 'Chair', caption: 'Chair Work' },
    { src: '/images/LADDER BARREL HERO 1.jpg', alt: 'Ladder Barrel', caption: 'Ladder Barrel' },
    { src: '/images/CAD.jpg', alt: 'Cadillac', caption: 'Cadillac' },
  ];

  return (
    <PageWrapper>
      {/* Hero */}
      <section data-testid="sessions-hero" className="py-24 md:py-32">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <FadeUp>
              <p className="text-xs uppercase tracking-[0.2em] text-stone-500 mb-4">Services</p>
              <h1 className="font-heading text-5xl sm:text-6xl text-stone-900 leading-[1.1] tracking-tight">
                1:1 <span className="italic">Sessions</span>
              </h1>
              <p className="mt-8 text-stone-600 text-lg leading-relaxed font-light">
                Private One-on-One Pilates — each session is fully customised and taught 
                in a private studio environment.
              </p>
            </FadeUp>
            <FadeUp delay={0.2}>
              <div className="image-zoom aspect-[4/3]">
                <img
                  src="/images/REFORMER SNAKE.jpg"
                  alt="Private Session"
                  className="w-full h-full object-cover editorial-image"
                />
              </div>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-24 md:py-32 bg-stone-100">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <StaggerContainer className="grid md:grid-cols-2 gap-6">
            {benefits.map((benefit, index) => (
              <StaggerItem key={index}>
                <div className="flex items-start gap-4 p-6 bg-white border border-stone-200">
                  <CheckCircle className="text-stone-900 flex-shrink-0 mt-1" size={20} strokeWidth={1.5} />
                  <p className="text-stone-600 leading-relaxed font-light">{benefit}</p>
                </div>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </section>

      {/* Image Carousel */}
      <section className="py-24 md:py-32">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <FadeUp>
            <ImageCarousel images={images} />
          </FadeUp>
        </div>
      </section>

      {/* Session Details */}
      <section data-testid="session-details" className="py-24 md:py-32 bg-stone-900 text-white">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <SlideLeft>
              <div className="image-zoom aspect-[4/5]">
                <img
                  src="/images/CHAIR PIKE.jpg"
                  alt="Session"
                  className="w-full h-full object-cover editorial-image"
                />
              </div>
            </SlideLeft>

            <SlideRight>
              <p className="text-xs uppercase tracking-[0.2em] text-stone-400 mb-4">
                Private Training
              </p>
              <h2 className="font-heading text-4xl sm:text-5xl text-white leading-[1.1] tracking-tight">
                Sessions <span className="italic">Focus On</span>
              </h2>
              <ul className="mt-8 space-y-4">
                {sessionFocus.map((item) => (
                  <li key={item} className="flex items-start gap-3 text-stone-300 font-light">
                    <CheckCircle className="flex-shrink-0 mt-0.5" size={18} strokeWidth={1.5} />
                    {item}
                  </li>
                ))}
              </ul>
              <p className="mt-8 text-stone-400 italic font-light">
                This is focused work, guided with care and clarity.
              </p>
              <Button
                asChild
                data-testid="sessions-book-btn"
                className="mt-8 bg-white text-stone-900 hover:bg-stone-100 rounded-none px-8 py-3 uppercase tracking-widest text-xs font-medium"
              >
                <Link to="/bookings">
                  Book a Session
                  <ArrowRight className="ml-2" size={16} strokeWidth={1.5} />
                </Link>
              </Button>
            </SlideRight>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 text-center">
          <FadeUp>
            <h2 className="font-heading text-4xl sm:text-5xl text-stone-900 leading-[1.1] tracking-tight">
              Ready to Start?
            </h2>
            <div className="mt-10 flex flex-wrap justify-center gap-4">
              <Button asChild data-testid="sessions-cta-book" className="btn-primary">
                <Link to="/bookings">Book Now</Link>
              </Button>
              <Button asChild variant="outline" data-testid="sessions-cta-pricing" className="btn-secondary">
                <Link to="/pricing">View Pricing</Link>
              </Button>
            </div>
          </FadeUp>
        </div>
      </section>
    </PageWrapper>
  );
};

export default SessionsPage;
